import React from 'react';
import type { CompanyEarningsWidgetInstanceConfig, CompanyEarningsData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, formatPrice, formatFloat } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: CompanyEarningsData = {
  ticker: 'SAMPLE',
  next_earnings_date: '2026-05-01',
  next_earnings_period: '2Q26',
  next_reporting_time: 'AMC',
  next_is_confirmed: 'Y',
  last_earnings_date: '2026-01-30',
  last_earnings_period: '1Q26',
  consensus_next_eps: 2.14,
  consensus_next_revenue: 94_800_000_000,
  consensus_last_eps: 1.95,
  consensus_last_revenue: 89_500_000_000,
  actual_last_eps: 2.03,
  actual_last_revenue: 91_200_000_000,
};

interface Props {
  config: CompanyEarningsWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

// Format a YYYY-MM-DD date to "May 1, 2026" + days-until/since
function formatEarningsDate(iso: string | null): { display: string; relative: string | null } {
  if (!iso) return { display: '--', relative: null };
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return { display: iso, relative: null };
  const display = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  let relative: string | null = null;
  if (diff === 0) relative = 'Today';
  else if (diff > 0) relative = `in ${diff}d`;
  else relative = `${Math.abs(diff)}d ago`;
  return { display, relative };
}

function reportingTimeLabel(code: string | null): string {
  if (!code) return '';
  const u = code.toUpperCase();
  if (u === 'BMO') return 'Before Open';
  if (u === 'AMC') return 'After Close';
  if (u === 'DMT') return 'During Market';
  return code;
}

function formatEps(v: number | null): string {
  if (v == null) return '--';
  return `$${formatFloat(v, 2)}`;
}

/** Compute surprise (actual - consensus) / consensus as a signed percent */
function surprisePct(actual: number | null, consensus: number | null): number | null {
  if (actual == null || consensus == null || consensus === 0) return null;
  return ((actual - consensus) / Math.abs(consensus)) * 100;
}

function surpriseColor(v: number | null): string {
  if (v == null) return 'text-neutral-500';
  return v >= 0 ? 'text-emerald-600' : 'text-red-600';
}

const CompanyEarningsWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<CompanyEarningsData>(
    'company_earnings',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (!displayData) return null;

  const next = formatEarningsDate(displayData.next_earnings_date);
  const last = formatEarningsDate(displayData.last_earnings_date);
  const epsSurprise = surprisePct(displayData.actual_last_eps, displayData.consensus_last_eps);
  const revSurprise = surprisePct(displayData.actual_last_revenue, displayData.consensus_last_revenue);
  const confirmed = (displayData.next_is_confirmed || '').toUpperCase() === 'Y';

  return (
    <div className={`h-full flex flex-col overflow-hidden bg-surface ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}

      {/* Header: ticker + next earnings headline */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-sm font-semibold text-neutral-800">{displayData.ticker}</span>
        <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide">Earnings</span>
        {displayData.next_earnings_period && (
          <span className="text-xs font-medium text-neutral-600">{displayData.next_earnings_period}</span>
        )}
        {next.relative && (
          <span className="ml-auto text-xs font-medium text-primary-600">{next.relative}</span>
        )}
      </div>

      {/* Upcoming earnings block */}
      <div className="px-3 py-3 border-b border-neutral-100">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-lg font-semibold text-neutral-800">{next.display}</span>
          {displayData.next_reporting_time && (
            <span className="text-xs text-neutral-500">
              {reportingTimeLabel(displayData.next_reporting_time)}
            </span>
          )}
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${
              confirmed
                ? 'bg-success-50 text-success-700'
                : 'bg-warning-50 text-warning-700'
            }`}
          >
            {confirmed ? 'Confirmed' : 'Estimated'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide mb-0.5">Consensus EPS</div>
            <div className="text-sm font-semibold text-neutral-800 tabular-nums">
              {formatEps(displayData.consensus_next_eps)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide mb-0.5">Consensus Revenue</div>
            <div className="text-sm font-semibold text-neutral-800 tabular-nums">
              {formatPrice(displayData.consensus_next_revenue)}
            </div>
          </div>
        </div>
      </div>

      {/* Last reported block with surprise */}
      <div className="px-3 py-3 flex-1 overflow-auto">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide">Last Reported</span>
          {displayData.last_earnings_period && (
            <span className="text-xs font-medium text-neutral-600">{displayData.last_earnings_period}</span>
          )}
          {last.display !== '--' && (
            <span className="text-xs text-neutral-500">— {last.display}</span>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-neutral-400 uppercase tracking-wide">
              <th className="text-left font-medium py-1"></th>
              <th className="text-right font-medium py-1">Est.</th>
              <th className="text-right font-medium py-1">Actual</th>
              <th className="text-right font-medium py-1">Surprise</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-neutral-100">
              <td className="py-1.5 text-neutral-500 font-medium">EPS</td>
              <td className="py-1.5 text-right text-neutral-700 tabular-nums">{formatEps(displayData.consensus_last_eps)}</td>
              <td className="py-1.5 text-right text-neutral-800 font-semibold tabular-nums">{formatEps(displayData.actual_last_eps)}</td>
              <td className={`py-1.5 text-right font-semibold tabular-nums ${surpriseColor(epsSurprise)}`}>
                {epsSurprise == null ? '--' : `${epsSurprise >= 0 ? '+' : ''}${epsSurprise.toFixed(1)}%`}
              </td>
            </tr>
            <tr className="border-t border-neutral-100">
              <td className="py-1.5 text-neutral-500 font-medium">Revenue</td>
              <td className="py-1.5 text-right text-neutral-700 tabular-nums">{formatPrice(displayData.consensus_last_revenue)}</td>
              <td className="py-1.5 text-right text-neutral-800 font-semibold tabular-nums">{formatPrice(displayData.actual_last_revenue)}</td>
              <td className={`py-1.5 text-right font-semibold tabular-nums ${surpriseColor(revSurprise)}`}>
                {revSurprise == null ? '--' : `${revSurprise >= 0 ? '+' : ''}${revSurprise.toFixed(1)}%`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyEarningsWidgetRenderer;
